/**
 * versona_compare.ts — civ A against civ B, side by side (2026-09-20).
 *
 * Every other card answers "what is the world doing". This one answers "which
 * half is doing better", which is the question an admin of a two-civ world
 * actually asks and which a mean over both civs can never show: two halves
 * pulling in opposite directions average out to a calm world.
 *
 * Pure and deliberately DUMB — it takes two already-reduced sides and formats
 * them. The module does the civ scoping, because only the module knows which
 * settlement flies which flag.
 */
import { type MetricGroup, int, f1, pct } from './versona_metrics.js';

export interface CivSide {
  civ: string;
  population: number; settlements: number; agents: number; wallet: number;
  offices: number; scoreMean: number; graves: number;
  crimesTotal: number; crimesOpen: number; guilty: number;
  purse: number; legitimacy: number; members: number; suspended: number; defiant: number;
  atWar: boolean;
}

export function emptyCivSide(civ: string): CivSide {
  return {
    civ, population: 0, settlements: 0, agents: 0, wallet: 0, offices: 0, scoreMean: 0, graves: 0,
    crimesTotal: 0, crimesOpen: 0, guilty: 0, purse: 0, legitimacy: 0, members: 0, suspended: 0, defiant: 0, atWar: false,
  };
}

const share = (n: number, d: number): number => (d > 0 ? n / d : 0);

/** Null when neither side has anyone — a comparison of two empty halves says nothing. */
export function compareCivs(a: CivSide, b: CivSide): MetricGroup | null {
  if (a.population + b.population === 0) return null;
  const pair = (label: string, x: string, y: string, hint?: string): { label: string; a: string; b: string; hint?: string } =>
    ({ label, a: x, b: y, hint });
  const decided = (s: CivSide): number => s.crimesTotal - s.crimesOpen;
  return {
    id: 'civ-compare', cat: 'gov', title: `Civ ${a.civ.toUpperCase()} against civ ${b.civ.toUpperCase()}`,
    columns: [`civ ${a.civ}`, `civ ${b.civ}`],
    metrics: [],
    pairs: [
      pair('people born to it', int(a.population), int(b.population), 'by BIRTH civ, not by where they live now — a migrant still counts to the flag they were born under'),
      pair('settlements', int(a.settlements), int(b.settlements)),
      pair('coins in pockets', int(a.wallet), int(b.wallet)),
      pair('mean purse per head', f1(share(a.wallet, a.agents)), f1(share(b.wallet, b.agents))),
      pair('offices held', int(a.offices), int(b.offices)),
      pair('town score (mean)', f1(a.scoreMean), f1(b.scoreMean), 'the seven rolling scores an activity feeds, averaged over the civ’s towns'),
      pair('graves', int(a.graves), int(b.graves)),
      pair('crime cases', int(a.crimesTotal), int(b.crimesTotal)),
      pair('still open', int(a.crimesOpen), int(b.crimesOpen)),
      pair('conviction rate', decided(a) > 0 ? pct(share(a.guilty, decided(a))) : '—', decided(b) > 0 ? pct(share(b.guilty, decided(b))) : '—', 'of the cases that reached a verdict'),
      pair('federal purse', int(a.purse), int(b.purse)),
      pair('legitimacy', pct(a.legitimacy), pct(b.legitimacy), 'under 25 % only relief can pass'),
      pair('members · suspended · defiant', `${int(a.members)} · ${int(a.suspended)} · ${int(a.defiant)}`, `${int(b.members)} · ${int(b.suspended)} · ${int(b.defiant)}`),
      pair('at war', a.atWar ? 'YES' : 'no', b.atWar ? 'YES' : 'no'),
    ],
  };
}
